import {
  ApolloClient,
  ApolloError,
  FieldPolicy,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';
import generatedIntrospection from '../graphql/possibleTypes.generated';

type KeyArgs = FieldPolicy<any>['keyArgs'];

const offsetLimitPagination = <T extends { items: unknown[] }>(
  keyArgs: KeyArgs = false,
): FieldPolicy<T> => ({
  keyArgs,
  merge: (existing, incoming, { variables }) => ({
    ...(existing ?? []),
    ...incoming,
    items: [
      ...(existing?.items.slice(0, variables?.skip) ?? []),
      ...(incoming?.items ?? []),
    ],
  }),
});

const cache = new InMemoryCache({
  possibleTypes: generatedIntrospection.possibleTypes,
  typePolicies: {
    Query: {
      fields: {
        reviewCollection: offsetLimitPagination([
          'preview',
          'locale',
          'where',
          'order',
        ]),
      },
    },
    Review: { keyFields: ['sys', ['id']] },
    Agent: { keyFields: ['sys', ['id']] },
  },
});

const httpLink = new HttpLink({
  uri: `https://graphql.contentful.com/content/v1/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID}`,
  headers: {
    Authorization: `Bearer ${process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN}`,
  },
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  if (networkError) console.error(`[Network error]: ${networkError}`);
});

export const apolloClient = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache,
  ssrMode: false,
  assumeImmutableResults: true,
  connectToDevTools: true,
});

export const apolloClientSsr = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache,
  ssrMode: true,
  assumeImmutableResults: true,
});

// Used by GraphQL Code Generator for graphql-codegen-apollo-next-ssr plugin
export const getApolloClient = (ctx: any) => apolloClientSsr;

export type GetServerPageProps<
  T extends (
    options: any,
    ctx?: any,
  ) => Promise<{
    props: {
      apolloState: NormalizedCacheObject;
      data: any;
      error: ApolloError | readonly GraphQLError[] | null;
    };
  }>,
> = Awaited<ReturnType<T>>['props'];
